import React, { useContext, useState, useMemo, useRef } from 'react';

import { ALERT_TYPE, CUENTA_STATE, MASK_FORMAT, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, TRIBUTO_TYPE } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { ServerRequest } from '../../utils/apiweb';
import { useAccess, useForm, useFilterPersistence, useNavigateWithFilters } from '../../components/hooks';
import { createSearchWithSavedFilters } from '../../utils/searchWithSavedFilters';
import {
    Loading,
    TableCustom,
    SectionHeading,
    InputPersona,
    AdvancedSearch,
    InputWithEnterEvent,
    WrapperAdvancedSearch
} from '../../components/common';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import CrearCuentaModal from '../../components/controls/CrearCuentaModal';
import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { ManagedContext } from '../../context/custom';
import { useLista } from '../../components/hooks/useLista';
import {usePermissions} from "../../components/hooks/usePermissions";
import {PERMISSIONS} from "../../consts/permissions";
import {TIPS} from "../../consts/tips";
import { parseSerializedIds } from '../../utils/convert';
import { buildEspecialCreateBody, ESPECIAL_GATE_INIT, isEspecialGateValid } from '../EspecialView/utils';
import EspecialGateFields from '../EspecialView/components/EspecialGateFields';


function EspecialesView() {

    //hooks
    const { showMessageModal } = useContext(ManagedContext)

    const [state, setState] = useState({
        loading: false,
        dataTagger: {
            showModal: false,
            idEntidad: null
        },
        list: []
    });

    const [filters, setFilters] = useState({
        labels: [
            { title: 'Persona', field: 'descPersona', value: '', valueIgnore: '', fieldOriginal: 'idPersona', valueIgnoreOriginal: 0},
            { title: 'N° cuenta', field: 'numeroCuenta', value: '', valueIgnore: '' },
            { title: 'Etiqueta', field: 'etiqueta', value: '', valueIgnore: '' },
            { title: 'Condición especial', field: 'idsTipoCondicionEspecial', type: 'entityMultiple', entidad: 'TipoCondicionEspecial', size: 3 },
            { title: 'Recargo descuento', field: 'idsTipoRecargoDescuento', type: 'entityMultiple', entidad: 'TipoRecargoDescuento', size: 3 },
            { title: 'Solo activos', field: 'descCuentasActivas', value: 'No', valueIgnore: 'No', fieldOriginal: 'idEstadoCuenta', valueIgnoreOriginal: 0}
        ]
    });

    const [tableVisible, setTableVisible] = useState(false)
    const [searchMessage, setSearchMessage] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createFormValues, , createFormReset, createFormSet] = useForm(ESPECIAL_GATE_INIT);

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.CUENTA_ESPECIAL_EDIT);
    const canReactivar = hasPermission(PERMISSIONS.CUENTA_EDIT);

    const searchRef = useRef();
    const tableRef = useRef();

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
          const message = error.message;
          ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
          setState(prevState => {
            return {...prevState, loading: false};
          });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
              return {...prevState, loading: false};
            });
        });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
          return {...prevState, loading: false};
        });
    }

    const SearchEspecialesWithSavedFilters = createSearchWithSavedFilters({
        setState,
        setTableVisible,
        apiUrl: APIS.URLS.ESPECIAL,
        buildParamsUrl: (filters) => 
            `/cuenta/filter?` +
            `numeroCuenta=${encodeURIComponent(filters.numeroCuenta)}&` +
            `idEstadoCuenta=${filters.idEstadoCuenta}&` +
            `numeroWeb=&` +
            `numeroDocumento=&` +
            `idPersona=${filters.idPersona}&` +
            `etiqueta=${encodeURIComponent(filters.etiqueta)}&` +
            `idsTipoCondicionEspecial=${encodeURIComponent(parseSerializedIds(filters.idsTipoCondicionEspecial))}&` +
            `idsTipoRecargoDescuento=${encodeURIComponent(parseSerializedIds(filters.idsTipoRecargoDescuento))}`,
        callbackNoSuccess,
        callbackError,
        setSearchMessage
    });

    const { getCurrentFilters, restoredPageIndex } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchEspecialesWithSavedFilters
    );
    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters);

    //definiciones
    const cellA = (props) => !canEdit ? '' : (
        <div className='action'>
            <div onClick={ (event) => handleClickEspecialAdd() } className="link">
                <span className="material-symbols-outlined" title="Nuevo">add</span>
            </div>
        </div>
    )
    const cellVMR = (props) => (
        <div className='action'>
            <div onClick={ (event) => handleClickEspecialView(props.value) } className="link">
                <span className="material-symbols-outlined" title="Ver">search</span>
            </div>
            {canEdit && props.row.original.idEstadoCuenta !== CUENTA_STATE.INACTIVA && <>
                <div onClick={ (event) => handleClickEspecialModify(props.value) } className="link">
                <span className="material-symbols-outlined" title="Modificar">stylus</span>
                </div>
                <div onClick={ (event) => handleClickEspecialRemove(props.value) } className="link">
                <span className="material-symbols-outlined" title="Eliminar">delete</span>
                </div>
            </>}
            {canReactivar && props.row.original.idEstadoCuenta === CUENTA_STATE.INACTIVA &&
                <div onClick={ (event) => handleClickReactivar(props.row.original.idCuenta, props.row.original.numeroCuenta) } className="link">
                <span className="material-symbols-outlined" title="Reactivar">unarchive</span>
                </div>
            }
            <div onClick={ (event) => handleClickEspecialDataTagger(props.value) } className="link">
            <span className="material-symbols-outlined" title="Información adicional">info</span>
            </div>
        </div>
    )
   
    const [, getRowLista ] = useLista({
        listas: ['EstadoCuenta'],
        onLoaded: (listas, isSuccess, error) => {
        if (isSuccess) {

        }
        else {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
        }
        },
        memo: {
        key: 'EstadoCuenta',
        timeout: 0
        }
    });

    const getDescEstadoCuenta = (id) => {
        const row = getRowLista('EstadoCuenta', id);
        return (row) ? row.nombre : '';
    }

    const tableColumns = [
        { Header: 'N° cuenta', accessor: 'numeroCuenta', width: '20%' },
        { Header: 'Detalle', accessor: 'id', width: '40%' },
        { Header: 'Estado cuenta', Cell: (props) => { return getDescEstadoCuenta(props.value); }, accessor: 'idEstadoCuenta', width: '15%' },
        { Header: cellA, Cell: cellVMR, id:'abm', accessor: 'id', width: '10%', disableGlobalFilter: true, disableSortBy: true }
    ];

    //handles
    const handleClickEspecialAdd = () => {
        createFormReset();
        setShowCreateModal(true);
    }
    const handleDismissCreateModal = () => {
        setShowCreateModal(false);
        createFormReset();
    }
    function CreateEspecial() {
        setState(prevState => ({ ...prevState, loading: true }));
        setShowCreateModal(false);

        const callbackSuccess = (response) => {
            response.json()
                .then((row) => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    navigateWithFilters(`/cuenta-especial/${OPERATION_MODE.EDIT}/${row.especial.id}`);
                })
                .catch((error) => {
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };
        ServerRequest(
            REQUEST_METHOD.POST, null, true,
            APIS.URLS.ESPECIAL, null,
            buildEspecialCreateBody(createFormValues),
            callbackSuccess, callbackNoSuccess, callbackError
        );
    }
    const handleClickEspecialView = (id) => {
        navigateWithFilters(`/cuenta-especial/${OPERATION_MODE.VIEW}/${id}`);
    }
    const handleClickEspecialModify = (id) => {
        navigateWithFilters(`/cuenta-especial/${OPERATION_MODE.EDIT}/${id}`);
    }
    const handleClickEspecialRemove = (id) => {
        const item = state.list.find(x => x.id === id);
        showMessageModal({
            ...MESSAGE_MODALS.BORRAR,
            message: `¿Está seguro de borrar el número de cuenta ${item.numeroCuenta}?`,
            onConfirm: () => RemoveEspecial(id),
        })
    }
    const handleClickReactivar = (idCuenta, numeroCuenta) => {
        showMessageModal({
            title: "Confirmación",
            message: `¿Está seguro de reactivar el número de cuenta ${numeroCuenta}?`,
            onConfirm: () => ReactivarCuenta(idCuenta),
        })
    }
    const handleClickEspecialDataTagger = (id) => {
        setState(prevState => {
          return {...prevState, dataTagger: {showModal: true, idEntidad: id}};
        });
    }

    //funciones
    function SearchEspeciales() {
        return SearchEspecialesWithSavedFilters(getCurrentFilters());
    }

    function RemoveEspecial(id) {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
            .then((id) => {
                setState(prevState => {
                    return {...prevState, loading: false};
                });
                SearchEspeciales();
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.ESPECIAL,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );

    }

    function ReactivarCuenta(idCuenta) {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
            .then(() => {
                setState(prevState => {
                    return {...prevState, loading: false};
                });
                ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'La cuenta fue reactivada correctamente');
                SearchEspeciales();
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
        };

        const paramsUrl = `/reactivar/${idCuenta}`;

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.CUENTA,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );

    }

    const dataTaggerRow = state.list.find(x => x.id === state.dataTagger.idEntidad)
    const dataTaggerRegistro = dataTaggerRow ? `la cuenta ${dataTaggerRow.numeroCuenta}` : ''

    return (
    <>

        <Loading visible={state.loading}></Loading>

        {state.dataTagger.showModal &&
            <DataTaggerModalApi
                title="Información adicional de cuenta especial"
                registro={dataTaggerRegistro}
                tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_CUENTA_ESPECIAL_MODAL}
                entidad="Especial"
                idEntidad={state.dataTagger.idEntidad}
                disabled={!canEdit}
                onConfirm={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
                onDismiss={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
            />
        }

        {showCreateModal && (
            <CrearCuentaModal
                canConfirm={isEspecialGateValid(createFormValues)}
                onConfirm={CreateEspecial}
                onDismiss={handleDismissCreateModal}
            >
                <EspecialGateFields
                    gate={createFormValues}
                    onChange={(patch) => createFormSet({ ...createFormValues, ...patch })}
                />
            </CrearCuentaModal>
        )}

        <SectionHeading titles={[{ title: 'Tributos' }, { title: 'Administración de cuentas especiales' }]}
                        onAddClick={canEdit ? handleClickEspecialAdd : null} tableVisible={tableVisible} />

        <section className='section-accordion'>
            <WrapperAdvancedSearch
                ref={searchRef}
                message={searchMessage}
                onClear={() => {
                    setState(prevState => ({ ...prevState, list: [] }));
                    setTableVisible(false);
                }}
                requireFilter
                onSearch={SearchEspeciales}
                filters={[
                    { title: 'Número de cuenta', field: 'numeroCuenta', type: 'string', mask: MASK_FORMAT.CUENTA, size: 3, searchOnEnter: true, },
                    { title: 'Persona', field: 'idPersona', type: 'persona', size: 5 },
                    { title: 'Estado', field: 'idEstadoCuenta', type: 'selectLista', lista: 'EstadoCuenta', presetValue: 2, presetLabel: 'Activa', size: 2 },
                    { title: 'Etiqueta', field: 'etiqueta', type: 'string', size: 2, searchOnEnter: true, },
                    { title: 'Condición especial', field: 'idsTipoCondicionEspecial', type: 'entityMultiple', entidad: 'TipoCondicionEspecial', filter: row => !row.idTipoTributo || row.idTipoTributo === TRIBUTO_TYPE.CUENTAS_ESPECIALES, size: 6 },
                    { title: 'Recargo descuento', field: 'idsTipoRecargoDescuento', type: 'entityMultiple', entidad: 'TipoRecargoDescuento', size: 6 },

                ]}
            />

            {tableVisible && (
                <TableCustom
                    ref={tableRef}
                    className={'TableCustomBase m-top-20'}
                    columns={tableColumns}
                    data={state.list}
                    restoredPageIndex={restoredPageIndex}
                    messageEmpty="No se encontraron resultados"
                />
            )}

        </section>
        
    </>
    )
}

export default EspecialesView;
